import React, { useState, useEffect } from "react";
import {
    Button,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    CircularProgress,
    Chip,
    InputAdornment,
    Avatar,
} from "@mui/material";
import CommonModal from "../../components/common/CommonModal";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "react-i18next";
import { StudentAccountsResponseDto, useUpdateStudentAccountAmount } from "../../hooks/useStudentAccounts";
import { useError } from "../../contexts/ErrorContext";
import { useAuth } from "../../contexts/AuthContext";

interface EditAccountModalProps {
    open: boolean;
    handleClose: () => void;
    account: StudentAccountsResponseDto | null;
}

const EditAccountModal: React.FC<EditAccountModalProps> = ({ open, handleClose, account }) => {
    const { t } = useTranslation();
    const { showError, showSuccess } = useError();
    const { user } = useAuth();
    const updateMutation = useUpdateStudentAccountAmount();

    const [updatedFeeAmount, setUpdatedFeeAmount] = useState<string>("");
    const [description, setDescription] = useState("");

    useEffect(() => {
        if (open && account) {
            setUpdatedFeeAmount(account.dueAmount != null ? account.dueAmount.toFixed(2) : "");
            setDescription("");
        }
    }, [open, account]);

    const handleSubmit = () => {
        if (!account) return;

        const parsed = parseFloat(updatedFeeAmount);
        if (isNaN(parsed) || parsed < 0) {
            showError("Please enter a valid amount");
            return;
        }

        const absDiff = Math.abs(diff).toFixed(2);
        const currentFormatted = account.dueAmount.toFixed(2);
        const autoMessage = diff < 0
            ? `Subtracted ${absDiff} from ${currentFormatted}`
            : `Added ${absDiff} to ${currentFormatted}`;
        const finalDescription = description.trim()
            ? `${autoMessage}. ${description.trim()}`
            : autoMessage;

        updateMutation.mutate(
            {
                studentAccountNumber: account.studentAccountNumber,
                student: account.student,
                feeAmount: account.dueAmount.toFixed(2),
                updatedFeeAmount: parsed.toFixed(2),
                description: finalDescription,
                updatedBy: user?.username || "",
            },
            {
                onSuccess: () => {
                    showSuccess(`Fee updated successfully for ${account.student?.studentName}`);
                    handleClose();
                },
                onError: (error: any) => {
                    showError(error?.response?.data?.message || "Error updating account");
                },
            }
        );
    };

    if (!account) return null;

    const parsedNew = parseFloat(updatedFeeAmount) || 0;
    const diff = parsedNew - (account.dueAmount || 0);

    return (
        <CommonModal
            open={open}
            onClose={handleClose}
            title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 44, height: 44 }}>
                        <EditNoteIcon sx={{ color: "#fff", fontSize: 24 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="800" color="inherit" lineHeight={1.2}>
                            Edit Fee Amount
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                            Update the due fee for this account
                        </Typography>
                    </Box>
                </Box>
            }
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden" } }}
            contentSx={{ px: 3.5, paddingTop: '24px !important', pb: 1 }}
            customActions={
                <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
                    <Button
                        fullWidth
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: 3,
                            py: 1.4,
                            borderColor: "#CFD8DC",
                            color: "text.secondary",
                            "&:hover": { borderColor: "#B0BEC5", bgcolor: "#F5F7FA" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={updateMutation.isLoading || !updatedFeeAmount}
                        sx={{
                            fontWeight: 800,
                            textTransform: "none",
                            borderRadius: 3,
                            py: 1.4,
                            bgcolor: "#1565C0",
                            boxShadow: "0 4px 14px rgba(21,101,192,0.35)",
                            "&:hover": { bgcolor: "#0D47A1", boxShadow: "0 6px 20px rgba(21,101,192,0.4)" },
                            "&.Mui-disabled": { bgcolor: "#B0BEC5" },
                        }}
                    >
                        {updateMutation.isLoading ? (
                            <CircularProgress size={22} color="inherit" />
                        ) : (
                            "Update Fee"
                        )}
                    </Button>
                </Box>
            }
        >
                {/* Student Info Row */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    <Box sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid #E3EAF5",
                        bgcolor: "#F5F8FF",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}>
                        <AccountCircleIcon sx={{ color: "#1565C0", fontSize: 28, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                                Student Name
                            </Typography>
                            <Typography variant="body2" fontWeight="700" noWrap>
                                {account.student?.studentName || "—"}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid #E3EAF5",
                        bgcolor: "#F5F8FF",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}>
                        <CreditCardIcon sx={{ color: "#1565C0", fontSize: 28, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                                Account No.
                            </Typography>
                            <Typography variant="body2" fontWeight="700" noWrap sx={{ fontSize: "0.75rem" }}>
                                {account.studentAccountNumber}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Current Due Amount Banner */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    mb: 3,
                    borderRadius: 3,
                    bgcolor: "#FFF3F3",
                    border: "1px solid #FFCDD2",
                }}>
                    <Box>
                        <Typography variant="caption" color="error.main" fontWeight={700} display="block">
                            Current Due Amount
                        </Typography>
                        <Typography variant="h5" color="error.main" fontWeight="900" lineHeight={1.2}>
                            ₹{account.dueAmount?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </Typography>
                    </Box>
                    {updatedFeeAmount && diff !== 0 && (
                        <Chip
                            size="small"
                            label={`${diff > 0 ? "+" : ""}₹${diff.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                            sx={{
                                fontWeight: 700,
                                bgcolor: diff > 0 ? "#E8F5E9" : "#E3F2FD",
                                color: diff > 0 ? "#2E7D32" : "#1565C0",
                                border: `1px solid ${diff > 0 ? "#A5D6A7" : "#90CAF9"}`,
                            }}
                        />
                    )}
                </Box>

                {/* Form Fields */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 1, color: "text.primary" }}>
                            New Fee Amount <Box component="span" color="error.main">*</Box>
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            value={updatedFeeAmount}
                            onChange={(e) => setUpdatedFeeAmount(e.target.value)}
                            inputProps={{ min: 0, step: "0.01" }}
                            placeholder="0.00"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography fontWeight={800} color="text.primary" fontSize="1.1rem">₹</Typography>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 3,
                                    fontSize: "1.15rem",
                                    fontWeight: 700,
                                    "& fieldset": { borderColor: "#CFD8DC" },
                                    "&:hover fieldset": { borderColor: "#1565C0" },
                                    "&.Mui-focused fieldset": { borderColor: "#1565C0", borderWidth: 2 },
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 1, color: "text.primary" }}>
                            Description
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Reason for update (e.g. fee revision, correction...)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 3,
                                    "& fieldset": { borderColor: "#CFD8DC" },
                                    "&:hover fieldset": { borderColor: "#1565C0" },
                                    "&.Mui-focused fieldset": { borderColor: "#1565C0", borderWidth: 2 },
                                },
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 1, color: "text.primary" }}>
                            Updated By
                        </Typography>
                        <TextField
                            fullWidth
                            value={user?.username || ""}
                            InputProps={{
                                readOnly: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon sx={{ color: "#90A4AE", fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 3,
                                    bgcolor: "#F5F7FA",
                                    "& fieldset": { borderColor: "#CFD8DC" },
                                    color: "text.secondary",
                                    fontWeight: 600,
                                },
                            }}
                        />
                    </Box>
                </Box>
        </CommonModal>
    );
};

export default EditAccountModal;
